import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/CrmApi";
import type {
  CrmPipeline,
  CrmOpportunity,
  CrmOpportunityDetail,
  OpportunityFilters,
  GatesFilter,
  RfpEventType,
} from "../api/CrmApi";

interface CrmContextValue {
  pipeline: CrmPipeline | null;
  pipelineLoaded: boolean;
  opportunities: CrmOpportunity[];
  stageCounts: Record<string, number>;
  loading: boolean;
  error: string;
  page: number;
  totalPages: number;
  totalCount: number;
  activeStageKey: string | null;
  search: string;
  gatesFilter: GatesFilter;

  setPage: (p: number) => void;
  setActiveStageKey: (key: string | null) => void;
  setSearch: (s: string) => void;
  setGatesFilter: (g: GatesFilter) => void;
  refresh: () => Promise<void>;

  loadDetail: (id: number) => Promise<CrmOpportunityDetail>;
  changeStage: (id: number, stageKey: string, notes?: string) => Promise<void>;
  addNote: (id: number, note: string) => Promise<void>;
  createOpportunity: (title: string, description?: string) => Promise<void>;
  deleteOpportunity: (id: number) => Promise<api.DeleteOpportunityResult>;
  recordRfpEvent: (id: number, event: RfpEventType, payload?: Record<string, unknown>) => Promise<void>;
}

const CrmContext = createContext<CrmContextValue | null>(null);

const PAGE_SIZE = 20;
// Single pipeline in practice — seeded by seeders.py, no UI path to create
// another. Looked up by its stable key rather than assuming sort order.
const SLED_PIPELINE_KEY = "sled_rfp";

export function CrmProvider({ children }: { children: React.ReactNode }) {
  const [pipeline, setPipeline] = useState<CrmPipeline | null>(null);
  const [pipelineLoaded, setPipelineLoaded] = useState(false);
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>([]);
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [activeStageKey, setActiveStageKeyState] = useState<string | null>(null);
  const [search, setSearchState] = useState("");
  const [gatesFilter, setGatesFilterState] = useState<GatesFilter>("");

  const setActiveStageKey = useCallback((key: string | null) => {
    setActiveStageKeyState(key);
    setPage(1);
  }, []);

  const setSearch = useCallback((s: string) => {
    setSearchState(s);
    setPage(1);
  }, []);

  const setGatesFilter = useCallback((g: GatesFilter) => {
    setGatesFilterState(g);
    setPage(1);
  }, []);

  const loadPipeline = useCallback(async () => {
    try {
      setPipeline(await api.getPipeline(SLED_PIPELINE_KEY));
    } catch {
      // 404 (not seeded yet) and any other failure both resolve to "no
      // pipeline" — CrmPage/CrmAnalyticsPage show an explanatory empty state.
      setPipeline(null);
    } finally {
      setPipelineLoaded(true);
    }
  }, []);

  // Monotonic request id — a stale (out-of-order) response must never
  // overwrite the results of a newer one (search fires on every keystroke).
  const reqSeq = useRef(0);

  const refresh = useCallback(async () => {
    if (!pipeline) return;
    const reqId = ++reqSeq.current;
    setLoading(true);
    setError("");
    try {
      const filters: OpportunityFilters = {
        pipeline_key: pipeline.key,
        page,
        page_size: PAGE_SIZE,
      };
      if (activeStageKey) filters.stage_key = activeStageKey;
      if (search.trim()) filters.search = search.trim();
      if (gatesFilter) filters.gates_verdict = gatesFilter;
      const [listRes, counts] = await Promise.all([
        api.listOpportunities(filters),
        api.getStageCounts(pipeline.key),
      ]);
      if (reqId !== reqSeq.current) return; // a newer request superseded this one
      setOpportunities(listRes.items);
      setTotalPages(listRes.total_pages);
      setTotalCount(listRes.total_count);
      setStageCounts(counts);
      // Results shrank below the current page (e.g. last item on the last
      // page deleted) — clamp back so the user never strands on an empty page.
      if (page > listRes.total_pages) setPage(listRes.total_pages);
    } catch {
      if (reqId === reqSeq.current) setError("Failed to load opportunities");
    } finally {
      if (reqId === reqSeq.current) setLoading(false);
    }
  }, [pipeline, page, activeStageKey, search, gatesFilter]);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);
  useEffect(() => { refresh(); }, [refresh]);

  const loadDetail = useCallback(async (id: number) => {
    return api.getOpportunity(id);
  }, []);

  const changeStage = useCallback(async (id: number, stageKey: string, notes?: string) => {
    await api.changeStage(id, stageKey, notes);
    await refresh();
  }, [refresh]);

  const addNote = useCallback(async (id: number, note: string) => {
    await api.addNote(id, note);
  }, []);

  const createOpportunity = useCallback(async (title: string, description?: string) => {
    if (!pipeline) {
      // Surface it — silently returning would close the modal as if it saved.
      throw new Error("The pipeline is still loading. Please try again in a moment.");
    }
    await api.createOpportunity({ pipeline_key: pipeline.key, title, description });
    await refresh();
  }, [pipeline, refresh]);

  const deleteOpportunity = useCallback(async (id: number) => {
    const result = await api.deleteOpportunity(id);
    await refresh();
    return result;
  }, [refresh]);

  const recordRfpEvent = useCallback(async (id: number, event: RfpEventType, payload: Record<string, unknown> = {}) => {
    await api.recordRfpEvent(id, event, payload);
    await refresh();
  }, [refresh]);

  const value = useMemo<CrmContextValue>(() => ({
    pipeline, pipelineLoaded, opportunities, stageCounts, loading, error,
    page, totalPages, totalCount, activeStageKey, search, gatesFilter,
    setPage, setActiveStageKey, setSearch, setGatesFilter, refresh,
    loadDetail, changeStage, addNote, createOpportunity, deleteOpportunity, recordRfpEvent,
  }), [
    pipeline, pipelineLoaded, opportunities, stageCounts, loading, error,
    page, totalPages, totalCount, activeStageKey, search, gatesFilter,
    setActiveStageKey, setSearch, setGatesFilter, refresh,
    loadDetail, changeStage, addNote, createOpportunity, deleteOpportunity, recordRfpEvent,
  ]);

  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
}

export function useCrm(): CrmContextValue {
  const ctx = useContext(CrmContext);
  if (!ctx) throw new Error("useCrm must be used within CrmProvider");
  return ctx;
}
